import { useStyletron } from '@tigergraph/app-ui-lib/Theme';
import { useLocation } from 'react-router-dom';
import { useMobile } from '@/contexts/mobileContext';
import { Menu } from 'lucide-react';
import IconButton from '@/components/IconButton';
import Logo from '@/pages/home/logo';

interface PageTitleProps {
  title?: string;
  showMenuButton?: boolean;
  onMenuClick?: () => void;
}

export default function PageTitle({ title, showMenuButton = true, onMenuClick }: PageTitleProps) {
  const [css, theme] = useStyletron();
  const location = useLocation();
  const { isMobile, toggleNavigation } = useMobile();

  // Auto-generate page title based on route if not provided
  const getPageTitle = () => {
    if (title) return title;

    const path = location.pathname;
    if (path === '/') return 'Home';
    if (path.includes('/editor')) return 'Query Editor';
    if (path.includes('/design-schema')) return 'Design Schema';
    if (path.includes('/ingestion')) return 'Load Data';
    if (path.includes('/explore')) return 'Explore Graph';
    if (path.includes('/groups')) return 'My Workgroups';
    if (path.includes('/marketplace/solutions')) return 'Solutions';
    if (path.includes('/marketplace/addons')) return 'Addons';
    if (path.includes('/admin/settings')) return 'Settings';
    if (path.includes('/admin/user')) return 'Users';
    if (path.includes('/admin/bill')) return 'Billing';
    if (path.includes('/admin/audit-log')) return 'Audit Log';

    return 'Page';
  };

  if (!isMobile) {
    return null; // Don't show page title on desktop
  }

  return (
    <div
      className={css({
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '16px',
        borderBottom: `1px solid ${theme.colors['border.tertiary']}`,
        backgroundColor: theme.colors['background.primary'],
        position: 'sticky',
        top: 0,
      })}
    >
      {showMenuButton && (
        <IconButton
          onClick={onMenuClick || toggleNavigation}
          className={css({
            flexShrink: 0,
          })}
        >
          <Menu size={20} />
        </IconButton>
      )}
      <h1
        className={css({
          fontSize: '16px',
          lineHeight: '24px',
          fontFamily: 'Urbanist',
          fontWeight: 600,
          color: theme.colors['text.primary'],
          margin: 0,
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          height: '24px',
        })}
      >
        <Logo />
        {getPageTitle()}
      </h1>
      {/* Placeholder element to keep title centered */}
      <div className={css({ width: '28px', flexShrink: 0 })} />
    </div>
  );
}
